// Dev Tools — Cloud Function routing override + Firestore/Auth database
// selection.
//
// State persists in localStorage so a developer's selection survives reloads
// without leaking to other browsers/users. Only this browser is affected;
// other clients (including production traffic from real users) ignore these
// overrides entirely — getFunctionUrl() reads localStorage on each call site.
//
// Visibility (panel show/hide) is decided in the component; this module just
// holds the routing state.

const LS_FUNCTIONS = "devTools/functions"
const LS_LOCAL_BASE = "devTools/localBase"
const LS_PANEL_OPEN = "devTools/panelOpen"
const LS_DATABASE = "devTools/database"
const LS_EMU_FIRESTORE = "devTools/emulatorFirestore"
const LS_EMU_AUTH = "devTools/emulatorAuth"

const DEFAULT_LOCAL_BASE = "http://127.0.0.1:5001/sld-etax/us-central1/"
// Repo-standard emulator ports (firebase/firebase.json). The e2e harness maps
// Auth to host 9098 because 9099 is commonly taken — see tests/e2e/docker-compose.yml.
const DEFAULT_EMU_FIRESTORE = "localhost:8084"
const DEFAULT_EMU_AUTH = "http://localhost:9098"

function readJsonLS(key, fallback) {
  if (typeof window === "undefined" || !window.localStorage) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return fallback
    return JSON.parse(raw)
  } catch (e) {
    return fallback
  }
}

function readStringLS(key, fallback) {
  if (typeof window === "undefined" || !window.localStorage) return fallback
  return window.localStorage.getItem(key) || fallback
}

function writeJsonLS(key, value) {
  if (typeof window === "undefined" || !window.localStorage) return
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // quota or disabled — silently ignore
  }
}

function writeStringLS(key, value) {
  if (typeof window === "undefined" || !window.localStorage) return
  try {
    window.localStorage.setItem(key, value)
  } catch (e) { /* */ }
}

export default {
  namespaced: true,
  state: () => ({
    overrides: readJsonLS(LS_FUNCTIONS, {}),
    localBase: readStringLS(LS_LOCAL_BASE, DEFAULT_LOCAL_BASE),
    panelOpen: readStringLS(LS_PANEL_OPEN, "false") === "true",
    // Firestore/Auth routing. Unlike the function overrides this one cannot be
    // applied live — db.settings() only takes effect before Firestore is first
    // used — so the component reloads the page after changing it.
    database: readStringLS(LS_DATABASE, "prod") === "emulator" ? "emulator" : "prod",
    emulatorFirestore: readStringLS(LS_EMU_FIRESTORE, DEFAULT_EMU_FIRESTORE),
    emulatorAuth: readStringLS(LS_EMU_AUTH, DEFAULT_EMU_AUTH),
  }),
  getters: {
    isLocal: (state) => (name) => state.overrides[name] === "local",
    activeCount: (state) => Object.values(state.overrides).filter((v) => v === "local").length,
  },
  mutations: {
    setOverride(state, { name, mode }) {
      if (mode === "local") {
        state.overrides = { ...state.overrides, [name]: "local" }
      } else {
        const next = { ...state.overrides }
        delete next[name]
        state.overrides = next
      }
      writeJsonLS(LS_FUNCTIONS, state.overrides)
    },
    setAll(state, { names, mode }) {
      const next = { ...state.overrides }
      names.forEach((name) => {
        if (mode === "local") next[name] = "local"
        else delete next[name]
      })
      state.overrides = next
      writeJsonLS(LS_FUNCTIONS, state.overrides)
    },
    clearAll(state) {
      state.overrides = {}
      writeJsonLS(LS_FUNCTIONS, state.overrides)
    },
    setLocalBase(state, base) {
      state.localBase = base || DEFAULT_LOCAL_BASE
      writeStringLS(LS_LOCAL_BASE, state.localBase)
    },
    setDatabase(state, mode) {
      state.database = mode === "emulator" ? "emulator" : "prod"
      writeStringLS(LS_DATABASE, state.database)
      // firebaseConfig reads the hosts straight from localStorage at boot and
      // falls back to the repo-standard ports (9099 auth) when a key is
      // missing. The panel shows the harness ports, so persist what it shows —
      // otherwise "Emulator" would silently connect somewhere else.
      writeStringLS(LS_EMU_FIRESTORE, state.emulatorFirestore)
      writeStringLS(LS_EMU_AUTH, state.emulatorAuth)
    },
    setEmulatorFirestore(state, host) {
      state.emulatorFirestore = host || DEFAULT_EMU_FIRESTORE
      writeStringLS(LS_EMU_FIRESTORE, state.emulatorFirestore)
    },
    setEmulatorAuth(state, url) {
      state.emulatorAuth = url || DEFAULT_EMU_AUTH
      writeStringLS(LS_EMU_AUTH, state.emulatorAuth)
    },
    setPanelOpen(state, open) {
      state.panelOpen = !!open
      writeStringLS(LS_PANEL_OPEN, state.panelOpen ? "true" : "false")
    },
  },
}
